import { useState, useCallback, useEffect, useRef } from 'react';
import type { Word, StageConfigEntry } from '../../types';
import { useQuickApproveStore } from '../../stores/quickApproveStore';
import { useUiStore } from '../../stores/uiStore';
import CardGrid from '../CardGrid';
import ApproveButton from '../ApproveButton';
import CompactToggle from '../CompactToggle';
import FieldCopyButton from '../FieldCopyButton';
import { resolveSelectedText } from '../../utils/resolveSelected';
import './StageCommon.css';

interface PickStageProps {
  word: Word;
  config: StageConfigEntry;
  taskName?: string;
  onGenerate: () => void;
  onCompare?: () => Promise<{ fields: Record<string, unknown>; model: string | null } | null>;
  onApprove: (selections: Record<string, unknown>) => void;
  onEdit?: (fieldId: string, newContent: string) => void;
  onRegenerateField?: (fieldName: string, instructions?: string) => Promise<{ value: string; model: string | null } | null>;
  lastModel?: string | null;
  generating: boolean;
  saving: boolean;
  registerApprovePayload: (fn: (() => Record<string, unknown> | null) | null) => void;
  factEvidence?: Stage4EvidenceRecord | null;
}

export interface FactEvidenceSource {
  url: string;
  host: string;
  sourceName: string;
  title: string;
  quote: string;
}

export interface Stage4EvidenceRecord {
  version: number;
  generatedAt: string;
  factsFound: number;
  searchNotes?: string;
  facts: Record<string, { category: string; sources: FactEvidenceSource[] }>;
}

export default function PickStage({ word, config, taskName, onGenerate, onCompare, onApprove, onEdit, onRegenerateField, lastModel, generating, saving, registerApprovePayload, factEvidence }: PickStageProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (config.selectedFields && config.cardFields) {
      const cardFields = config.cardFields;
      config.selectedFields.forEach((sf) => {
        const stored = word[sf];
        if (stored !== undefined && stored !== null && stored !== '') {
          const storedStr = String(stored);
          cardFields.forEach((cf) => {
            const m = cf.match(/_(\d+)$/);
            if (m && m[1] === storedStr) initial.add(cf);
          });
        }
      });
    }
    return initial;
  });

  const [compareData, setCompareData] = useState<Record<string, string> | null>(null);
  const [compareModel, setCompareModel] = useState<string | null>(null);
  const [comparing, setComparing] = useState<boolean>(false);
  const [regeneratingField, setRegeneratingField] = useState<string | null>(null);

  const { isQuickApproveOn, toggleQuickApprove, shouldShowNudge, dismissNudge } = useQuickApproveStore();
  const compactCards = useUiStore((s) => s.compactCards);
  const qaEnabled = taskName ? isQuickApproveOn(taskName) : false;
  const showNudge = taskName ? shouldShowNudge(taskName) : false;

  const isStage4 = config.generateEndpoint === '/api/generate/stage4';
  const zeroVerifiedFacts = isStage4 && word.facts_found === 0;
  const hasContent = (config.cardFields || []).some((f) => word[f]) || zeroVerifiedFacts;
  const selectionMet = zeroVerifiedFacts || (config.allowFewerSelections
    ? selectedIds.size > 0
    : selectedIds.size === config.requiredSelections);

  const handleSelect = useCallback((cardId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        if (next.size >= (config.requiredSelections || 0)) {
          const firstKey = next.values().next().value as string;
          next.delete(firstKey);
        }
        next.add(cardId);
      }
      return next;
    });
  }, [config.requiredSelections]);

  const handleEdit = useCallback((cardId: string, newContent: string) => {
    if (onEdit) onEdit(cardId, newContent);
  }, [onEdit]);

  // Is a selected card's underlying content non-empty? Original cards read from
  // word[field]; compare cards read from compareData[field]. An emptied pick is
  // excluded so it never persists as a phantom index (2026-07-05 M9 fix).
  const cardHasContent = useCallback((cardId: string): boolean => {
    const isCmp = cardId.startsWith('_cmp_');
    const rawField = isCmp ? cardId.slice(5) : cardId;
    const val = isCmp ? (compareData?.[rawField] ?? '') : String(word[rawField] ?? '');
    return val.trim() !== '';
  }, [compareData, word]);

  const buildSelections = useCallback((): Record<string, unknown> => {
    const selections: Record<string, unknown> = {};
    const selectedArray = Array.from(selectedIds).filter(cardHasContent);
    (config.selectedFields || []).forEach((sf, i) => {
      const cardId = selectedArray[i];
      if (cardId) {
        const match = cardId.match(/_(\d+)$/);
        if (match) selections[sf] = match[1];
      }
    });
    return selections;
  }, [selectedIds, config.selectedFields, cardHasContent]);

  // Persist any picked compare-column values into their underlying fields
  // BEFORE approving. A `_cmp_<field>` selection resolves to the same stored
  // index as the original card, so without this the chosen NEW text is lost and
  // the OLD value at that index is saved (2026-07-05 M3 fix). Writing the
  // compare value through onEdit makes "mix from either column" actually keep
  // the new text.
  const persistSelectedCompareValues = useCallback(() => {
    if (!compareData || !onEdit) return;
    for (const id of selectedIds) {
      if (!id.startsWith('_cmp_')) continue;
      const rawField = id.slice(5);
      const newVal = compareData[rawField];
      if (typeof newVal === 'string' && newVal !== String(word[rawField] ?? '')) {
        onEdit(rawField, newVal);
      }
    }
  }, [compareData, onEdit, selectedIds, word]);

  // Drop any selected card whose underlying content is now empty — an emptied
  // pick would otherwise persist as a phantom selection at that index
  // (2026-07-05 M9 fix). Original cards read from word[field]; compare cards
  // read from compareData[field].
  const pruneEmptySelections = useCallback(() => {
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        const isCmp = id.startsWith('_cmp_');
        const rawField = isCmp ? id.slice(5) : id;
        const val = isCmp ? (compareData?.[rawField] ?? '') : String(word[rawField] ?? '');
        if (val.trim() === '') { changed = true; continue; }
        next.add(id);
      }
      return changed ? next : prev;
    });
  }, [compareData, word]);

  const handleApprove = useCallback(() => {
    pruneEmptySelections();
    persistSelectedCompareValues();
    onApprove(buildSelections());
  }, [pruneEmptySelections, persistSelectedCompareValues, buildSelections, onApprove]);

  const handleQuickApprove = useCallback(() => {
    const autoSelected = (config.cardFields || []).slice(0, config.requiredSelections);
    const selections: Record<string, unknown> = {};
    (config.selectedFields || []).forEach((sf, i) => {
      const cardId = autoSelected[i];
      if (cardId) {
        const match = cardId.match(/_(\d+)$/);
        if (match) selections[sf] = match[1];
      }
    });
    onApprove(selections);
  }, [config, onApprove]);

  useEffect(() => {
    if (!registerApprovePayload) return;
    registerApprovePayload(() => {
      if (!selectionMet) return null;
      return buildSelections();
    });
  }, [registerApprovePayload, selectionMet, buildSelections]);

  const handleCompare = useCallback(async () => {
    if (!onCompare) return;
    setComparing(true);
    const res = await onCompare();
    if (res) {
      setCompareData(res.fields as Record<string, string>);
      setCompareModel(res.model);
    }
    setComparing(false);
  }, [onCompare]);

  const handleDismissCompare = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set<string>();
      for (const id of prev) {
        if (!id.startsWith('_cmp_')) next.add(id);
      }
      return next;
    });
    setCompareData(null);
    setCompareModel(null);
  }, []);

  const handleEditCompareField = useCallback((fieldId: string, newContent: string) => {
    setCompareData((prev) => (prev ? { ...prev, [fieldId]: newContent } : prev));
  }, []);

  const handleRegenerateOne = useCallback(async (fieldId: string, instructions?: string) => {
    if (!onRegenerateField) return;
    setRegeneratingField(fieldId);
    const res = await onRegenerateField(fieldId, instructions);
    if (res) {
      setCompareData((prev) => (prev ? { ...prev, [fieldId]: res.value } : { [fieldId]: res.value }));
      if (res.model) setCompareModel(res.model);
    }
    setRegeneratingField(null);
  }, [onRegenerateField]);

  // One-level regen undo: remember the value a field held BEFORE its last
  // regeneration (session-only, per word). Never set by the generate flow —
  // only by an actual regen of an existing value.
  const [undoValues, setUndoValues] = useState<Record<string, string>>({});
  useEffect(() => { setUndoValues({}); }, [word.id]);

  const handleRegenerateInline = useCallback(async (fieldId: string, instructions?: string) => {
    if (!onRegenerateField || regeneratingField) return;
    const prev = String(word[fieldId] ?? '');
    setRegeneratingField(fieldId);
    try {
      const res = await onRegenerateField(fieldId, instructions);
      if (res && onEdit) {
        if (!isStage4 && prev.trim()) setUndoValues((u) => ({ ...u, [fieldId]: prev }));
        onEdit(fieldId, res.value);
        if (isStage4) {
          setSelectedIds((current) => {
            const next = new Set(current);
            next.delete(fieldId);
            return next;
          });
        }
      }
    } finally {
      setRegeneratingField(null);
    }
  }, [onRegenerateField, regeneratingField, onEdit, word, isStage4]);

  // Restore the pre-regeneration value through the same write path a regen
  // uses (onEdit → dirty-tracked → persisted by save/approve).
  const handleUndoRegen = useCallback((fieldId: string) => {
    const prev = undoValues[fieldId];
    if (prev === undefined || !onEdit) return;
    onEdit(fieldId, prev);
    setUndoValues((u) => {
      const next = { ...u };
      delete next[fieldId];
      return next;
    });
  }, [undoValues, onEdit]);

  const cards = (config.cardFields || [])
    .filter((f) => word[f])
    .map((f) => ({
      id: f,
      content: String(word[f] || ''),
      label: formatFieldLabel(f),
    }));

  const compareCards = compareData
    ? (config.cardFields || [])
        .filter((f) => compareData[f])
        .map((f) => ({
          id: `_cmp_${f}`,
          content: compareData[f],
          label: formatFieldLabel(f),
        }))
    : [];

  const contextFields = (config.contextFields || [])
    .filter((f) => word[f])
    .map((f) => ({
      label: formatFieldLabel(f),
      value: resolveSelectedText(f, word) ?? String(word[f] || ''),
    }));

  return (
    <div className="stage-view">
      {!hasContent && (
        <div className="stage-generate-area">
          <button className="generate-btn" onClick={onGenerate} disabled={generating} title={`Generate ${config.label} (\u2318G)`}>
            {generating ? 'Generating...' : `Generate ${config.label}`}
          </button>
        </div>
      )}

      {hasContent && (
        <div className="stage-toolbar">
          {!isStage4 && (
            <button className="regenerate-btn" onClick={onGenerate} disabled={generating} title="Regenerate (\u2318R)">
              {generating ? 'Regenerating...' : 'Regenerate'}
            </button>
          )}
          {onCompare && (
            <button
              className="compare-btn"
              onClick={compareData ? handleDismissCompare : handleCompare}
              disabled={generating || comparing}
            >
              {comparing ? 'Comparing...' : compareData ? 'Exit Compare' : 'Compare'}
            </button>
          )}
          <CompactToggle />
          {taskName && !isStage4 && (
            <label className="qa-toggle">
              <input
                type="checkbox"
                checked={qaEnabled}
                onChange={(e) => toggleQuickApprove(taskName, e.target.checked)}
              />
              <span>Quick Approve</span>
            </label>
          )}
          {lastModel && (
            <span className="stage-model-label" title="Model that produced the stored content">
              Generated by <strong>{lastModel}</strong>
            </span>
          )}
        </div>
      )}

      {hasContent && qaEnabled && !compareData && !isStage4 && (
        <button className="qa-action-btn" onClick={handleQuickApprove} disabled={saving}>
          {saving ? 'Approving...' : 'Quick Approve'}
        </button>
      )}

      {showNudge && !qaEnabled && hasContent && !isStage4 && (
        <div className="qa-nudge">
          <span>You've approved the last 20 {config.label} outputs without editing. Enable Quick Approve for this stage?</span>
          <div className="qa-nudge-actions">
            <button className="qa-nudge-enable" onClick={() => toggleQuickApprove(taskName!, true)}>Enable</button>
            <button className="qa-nudge-dismiss" onClick={() => dismissNudge(taskName!)}>Not now</button>
          </div>
        </div>
      )}

      {hasContent && !compareData && (
        <>
          {isStage4 && (
            <div className={`fact-evidence-banner ${zeroVerifiedFacts ? 'fact-evidence-zero' : ''}`}>
              <strong>
                {zeroVerifiedFacts
                  ? 'No facts passed verification. This is a valid result.'
                  : `${Number(word.facts_found ?? cards.length)} verified fact${Number(word.facts_found ?? cards.length) === 1 ? '' : 's'} available.`}
              </strong>
              {word.search_notes && <span>{String(word.search_notes)}</span>}
              {zeroVerifiedFacts && (
                <>
                  <span>Approve to continue without adding a fun fact, or search again.</span>
                  <button className="fact-evidence-retry" onClick={onGenerate} disabled={generating}>
                    {generating ? 'Searching...' : 'Search Again'}
                  </button>
                </>
              )}
            </div>
          )}

          <CardGrid
            cards={cards}
            selectedIds={selectedIds}
            onSelect={handleSelect}
            onEdit={isStage4 ? undefined : handleEdit}
            onRegenerate={onRegenerateField ? handleRegenerateInline : undefined}
            regeneratingId={regeneratingField}
            undoableIds={isStage4 ? new Set() : new Set(Object.keys(undoValues))}
            onUndo={isStage4 ? undefined : handleUndoRegen}
            showCheckboxes={true}
            requiredSelections={config.requiredSelections}
            contextFields={contextFields}
            compact={compactCards}
          />

          {isStage4 && factEvidence && Object.keys(factEvidence.facts || {}).length > 0 && (
            <div className="fact-evidence-panel">
              <div className="fact-evidence-title">Verified source evidence</div>
              {Object.entries(factEvidence.facts).map(([field, evidence]) => (
                <div className="fact-evidence-item" key={field}>
                  <strong>{formatFieldLabel(field)}</strong>
                  <span className="fact-evidence-category">{evidence.category.replace(/_/g, ' ')}</span>
                  <ul>
                    {evidence.sources.map((source) => (
                      <li key={`${field}:${source.url}`}>
                        <a href={source.url} target="_blank" rel="noreferrer">{source.sourceName || source.title || source.host}</a>
                        {source.quote && <span className="fact-evidence-quote">“{source.quote}”</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          <ApproveButton
            onClick={handleApprove}
            disabled={!selectionMet || saving}
            loading={saving}
            quickApprove={!isStage4}
            onQuickApprove={isStage4 ? undefined : handleQuickApprove}
            label={zeroVerifiedFacts ? 'Approve No Facts' : 'Approve'}
          />
        </>
      )}

      {hasContent && compareData && (
        <div className="compare-mode">
          <div className="compare-counter">
            <span className={selectedIds.size === config.requiredSelections ? 'counter-complete' : ''}>
              {selectedIds.size} / {config.requiredSelections} selected (mix from either column)
            </span>
          </div>

          {contextFields.length > 0 && (
            <div className="card-context">
              {contextFields.map((field, i) => (
                <div key={i} className="context-field field-copy-host">
                  <span className="context-label">
                    {field.label}
                    <FieldCopyButton text={field.value} label={`Copy ${field.label}`} />
                  </span>
                  <span className="context-value content-text">{field.value}</span>
                </div>
              ))}
            </div>
          )}

          <div className="compare-columns">
            <div className="compare-column">
              <h3 className="compare-column-title">
                Original{lastModel ? <> · <strong>{lastModel}</strong></> : null}
              </h3>
              <div className="compare-cards">
                {cards.map((card) => (
                  <div
                    key={card.id}
                    className={`compare-card-wrap field-copy-host ${selectedIds.has(card.id) ? 'compare-selected' : ''}`}
                    onClick={() => handleSelect(card.id)}
                  >
                    <div className="compare-card-label">
                      {card.label}
                      <FieldCopyButton text={card.content} label={`Copy ${card.label}`} />
                    </div>
                    <EditableCompareCell
                      value={card.content}
                      onChange={(v) => handleEdit(card.id, v)}
                    />
                    {selectedIds.has(card.id) && <div className="compare-check">&#10003;</div>}
                  </div>
                ))}
              </div>
            </div>
            <div className="compare-column">
              <h3 className="compare-column-title">
                New Generation{compareModel ? <> · <strong>{compareModel}</strong></> : null}
              </h3>
              <div className="compare-cards">
                {compareCards.map((card) => {
                  const rawField = card.id.slice(5); // strip '_cmp_' prefix
                  return (
                    <div
                      key={card.id}
                      className={`compare-card-wrap compare-card-new field-copy-host ${selectedIds.has(card.id) ? 'compare-selected' : ''}`}
                      onClick={() => handleSelect(card.id)}
                    >
                      <div className="compare-card-label-row">
                        <div className="compare-card-label">
                          {card.label}
                          <FieldCopyButton text={card.content} label={`Copy ${card.label}`} />
                        </div>
                        {onRegenerateField && (
                          <button
                            className="compare-regen-field"
                            onClick={(e) => { e.stopPropagation(); handleRegenerateOne(rawField); }}
                            disabled={regeneratingField === rawField || generating}
                            title="Regenerate just this field"
                          >
                            {regeneratingField === rawField ? 'Regenerating...' : 'Regenerate'}
                          </button>
                        )}
                      </div>
                      <EditableCompareCell
                        value={card.content}
                        onChange={(v) => handleEditCompareField(rawField, v)}
                      />
                      {selectedIds.has(card.id) && <div className="compare-check">&#10003;</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <ApproveButton
            onClick={handleApprove}
            disabled={!selectionMet || saving}
            loading={saving}
          />
        </div>
      )}

      {generating && !hasContent && (
        <div className="stage-generating">
          <div className="generating-spinner" />
          <p>Generating {config.label}...</p>
        </div>
      )}
    </div>
  );
}

function formatFieldLabel(fieldName: string): string {
  return fieldName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/^Selected /, '')
    .replace(/Def /, 'Definition ');
}

function EditableCompareCell({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (ref.current && !focused) {
      ref.current.textContent = value;
    }
  }, [value, focused]);

  return (
    <div
      ref={ref}
      className="compare-card-content content-text compare-card-editable"
      contentEditable
      suppressContentEditableWarning
      onClick={(e) => e.stopPropagation()}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        const next = ref.current?.textContent ?? '';
        if (next !== value) onChange(next);
      }}
      spellCheck={false}
    />
  );
}
